import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Breadcrumbs,
  Button,
  Card,
  Chip,
  Collapse,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  Link as MuiLink,
  MenuItem,
  Pagination,
  Select,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { Link as RouterLink } from "react-router-dom";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { alpha, useTheme } from "@mui/material/styles";
import { Formik } from "formik";
import * as Yup from "yup";
import moment from "moment";

import ForumIcon from "@mui/icons-material/Forum";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import StopIcon from "@mui/icons-material/Stop";
import DeleteIcon from "@mui/icons-material/Delete";
import SettingsIcon from "@mui/icons-material/Settings";
import RefreshIcon from "@mui/icons-material/Refresh";
import ScheduleIcon from "@mui/icons-material/Schedule";
import BoltIcon from "@mui/icons-material/Bolt";
import InsightsIcon from "@mui/icons-material/Insights";
import TuneIcon from "@mui/icons-material/Tune";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { HashLink as Link } from "react-router-hash-link";

import {
  getMastodonTasks,
  crawlMastodonHashtag,
  killMastodonTask,
  deleteMastodonTask,
} from "../../services/mastodon_services";
import { getAllResultsCount } from "../../services/generic_services";
import MastodonSettingsPopupForm from "../MastodonSettingsPopupForm";
import KpiTile from "../results/KpiTile";
import renderStatus from "../common/StatusChip";
import getEnv from "../../config";
import { createSocket } from "../../services/socket";

const baseURL = getEnv("REACT_APP_API_URL");
const MASTODON_COLOR = "#6364FF";

const MastodonSchema = Yup.object().shape({
  instance: Yup.string().required("Required"),
  hashtag: Yup.string().required("Required"),
});

const SectionHeader = ({ title, action }) => (
  <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", mb: 1.5 }}>
    <Typography variant="h6">{title}</Typography>
    {action}
  </Box>
);

const TaskCard = ({ task, onRun, onStop, onSettings, onDelete }) => {
  const theme = useTheme();
  const isRunning = task.state === "RUNNING";
  const createdAt = task.created_at && task.created_at["$date"]
    ? moment(task.created_at["$date"]).format("YYYY-MM-DD HH:mm")
    : "";

  return (
    <Card
      sx={{
        p: 2,
        borderLeft: `3px solid ${MASTODON_COLOR}`,
        "&:hover": { boxShadow: `0 2px 12px ${alpha(MASTODON_COLOR, 0.15)}` },
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 2 }}>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
            <Link
              to={`/mastodon/results#${encodeURIComponent(task.hashtag_id)}`}
              style={{ color: theme.palette.text.primary, textDecoration: "none" }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {task.hashtag?.includes("#") ? task.hashtag : `#${task.hashtag}`}
              </Typography>
            </Link>
            <Typography variant="body2" color="text.secondary">
              @ {task.instance}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: "wrap" }}>
            {renderStatus(task.state)}
            {task.monitoring && (
              <Chip
                size="small"
                icon={<ScheduleIcon sx={{ fontSize: 14 }} />}
                label={`every ${task.monitoring_interval || 1}d`}
                variant="outlined"
              />
            )}
            {task.focused && (
              <Chip
                size="small"
                icon={<BoltIcon sx={{ fontSize: 14 }} />}
                label={`focused ≥ ${task.threshold ?? 0.5}`}
                variant="outlined"
              />
            )}
            {task.post_limit && (
              <Typography variant="caption" color="text.secondary">
                limit {task.post_limit}
              </Typography>
            )}
            {createdAt && (
              <Typography variant="caption" color="text.disabled">
                · added {createdAt}
              </Typography>
            )}
          </Stack>
        </Box>
        <Stack direction="row" spacing={0.5}>
          <Tooltip title="Run now">
            <span>
              <IconButton size="small" color="primary" onClick={() => onRun(task)} disabled={isRunning}>
                <PlayArrowIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Stop">
            <span>
              <IconButton size="small" color="primary" onClick={() => onStop(task)} disabled={!isRunning}>
                <StopIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Task settings">
            <IconButton size="small" onClick={() => onSettings(task)}>
              <SettingsIcon fontSize="small" color="primary" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete source and related results">
            <IconButton size="small" onClick={() => onDelete(task)}>
              <DeleteIcon fontSize="small" color="primary" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>
    </Card>
  );
};

const STATE_FILTERS = [
  { key: "ALL", label: "All", match: () => true },
  { key: "QUEUED", label: "Queued", match: (s) => s === "PENDING" || s === "QUEUED" },
  { key: "RUNNING", label: "Running", match: (s) => s === "RUNNING" },
  { key: "COMPLETED", label: "Completed", match: (s) => s === "SUCCESS" || s === "COMPLETED" },
  { key: "FAILED", label: "Failed", match: (s) => s === "FAILED" || s === "FAILURE" },
  { key: "KILLED", label: "Killed", match: (s) => s === "KILLED" || s === "REVOKED" || s === "CANCELED" },
];

const PAGE_SIZE = 10;

const MastodonTaskPage = () => {
  const [mastodonTasks, setMastodonTasks] = useState([]);
  const [activeTask, setActiveTask] = useState();
  const [openPopupForm, setOpenPopupForm] = useState(false);
  const [postsCount, setPostsCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [stateFilter, setStateFilter] = useState("ALL");
  const [taskSearch, setTaskSearch] = useState("");
  const [page, setPage] = useState(1);
  const [showCombine, setShowCombine] = useState(false);

  const updateTaskParams = (data) => {
    setMastodonTasks((prev) =>
      prev.map((task) =>
        task.hashtag_id === data.hashtag_id ? { ...task, ...data } : task
      )
    );
  };

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [tasks, counts] = await Promise.allSettled([
        getMastodonTasks(),
        getAllResultsCount(),
      ]);
      if (tasks.status === "fulfilled") setMastodonTasks(tasks.value || []);
      if (counts.status === "fulfilled") setPostsCount(counts.value?.mastodon || 0);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  useEffect(() => {
    if (!baseURL) return;
    const socket = createSocket();
    socket.on("mastodon_state_update", (msg) => {
      const { instance, hashtag, state } = msg;
      setMastodonTasks((prev) =>
        prev.map((t) =>
          t.instance === instance && t.hashtag === hashtag ? { ...t, state } : t
        )
      );
    });
    return () => socket.disconnect();
  }, []);

  const submitMastodonTask = async (values) => {
    try {
      await crawlMastodonHashtag(values);
    } catch (error) {
      console.log("Mastodon submit error", error);
    }
    setTimeout(() => fetchAll(), 2000);
  };

  const killTask = async (task) => {
    try {
      await killMastodonTask({ instance: task.instance, hashtag: task.hashtag });
    } catch (error) {
      console.log(error);
    }
  };

  const deleteTask = async (task) => {
    try {
      await deleteMastodonTask(task);
      setTimeout(() => fetchAll(), 2000);
    } catch (error) {
      console.log(error);
    }
  };

  const openSettings = (task) => {
    setActiveTask(null);
    setActiveTask(task);
    setOpenPopupForm(true);
  };

  const kpis = useMemo(() => {
    const total = mastodonTasks.length;
    const running = mastodonTasks.filter((t) => t.state === "RUNNING").length;
    const scheduled = mastodonTasks.filter((t) => t.monitoring).length;
    return { total, running, scheduled };
  }, [mastodonTasks]);

  const filteredTasks = useMemo(() => {
    const matcher = (STATE_FILTERS.find((f) => f.key === stateFilter) || STATE_FILTERS[0]).match;
    const q = taskSearch.trim().toLowerCase();
    return mastodonTasks.filter((t) => {
      if (!matcher(t.state)) return false;
      if (q) {
        const hay = `${t.hashtag || ""} ${t.instance || ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [mastodonTasks, stateFilter, taskSearch]);

  const paginatedTasks = useMemo(
    () => filteredTasks.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [filteredTasks, page]
  );

  return (
    <Box>
      {/* Breadcrumbs */}
      <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mb: 1.5 }}>
        <MuiLink component={RouterLink} to="/results" underline="hover" color="text.secondary">
          All Results
        </MuiLink>
        <Typography variant="body2" color="text.primary">Mastodon</Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: { xs: "flex-start", sm: "center" },
          justifyContent: "space-between",
          flexDirection: { xs: "column", sm: "row" },
          gap: 1,
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h4" sx={{ mb: 0.5 }}>
            Mastodon Crawling
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Monitor cybersecurity-relevant hashtags across Mastodon instances
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <ToggleButtonGroup value="tasks" exclusive size="small">
            <ToggleButton component={RouterLink} to="/mastodon/tasks" value="tasks">
              Tasks
            </ToggleButton>
            <ToggleButton component={RouterLink} to="/mastodon/results" value="results">
              Results
            </ToggleButton>
          </ToggleButtonGroup>
          <Tooltip title="Refresh">
            <IconButton onClick={fetchAll} size="small" disabled={loading}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>

      {/* KPI strip */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <KpiTile
            label="Hashtags"
            value={kpis.total}
            unit="tracked"
            accent={MASTODON_COLOR}
            icon={<ForumIcon fontSize="small" />}
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiTile
            label="Running"
            value={kpis.running}
            unit="now"
            accent="#4caf50"
            icon={<BoltIcon fontSize="small" />}
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiTile
            label="Scheduled"
            value={kpis.scheduled}
            unit="monitoring"
            accent="#ff9800"
            icon={<ScheduleIcon fontSize="small" />}
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KpiTile
            label="Posts"
            value={postsCount}
            unit="collected"
            accent={MASTODON_COLOR}
            icon={<InsightsIcon fontSize="small" />}
            navigateTo="/mastodon/results"
            loading={loading}
          />
        </Grid>
      </Grid>

      {/* Form + Tasks */}
      <Grid container spacing={3}>
        {/* Add new hashtag */}
        <Grid item xs={12} md={5} lg={4}>
          <Card sx={{ p: 2.5 }}>
            <SectionHeader title="New crawl" />
            <FormGroup>
              <Formik
                initialValues={{
                  instance: "mastodon.social",
                  hashtag: "cybersecurity",
                  hashtag2: "",
                  hashtag3: "",
                  combine_count: 1,
                  operator: "AND",
                  threshold: 0.05,
                  monitoring: false,
                  monitoring_interval: 3,
                  focused: true,
                  post_limit: 20,
                }}
                validationSchema={MastodonSchema}
                onSubmit={(values) => submitMastodonTask(values)}
              >
                {({ handleChange, handleBlur, handleSubmit, values, setFieldValue, isValid }) => (
                  <Stack spacing={2.5}>
                    <Stack spacing={1.5}>
                      <TextField
                        label="Instance"
                        placeholder="mastodon.social"
                        onChange={handleChange("instance")}
                        onBlur={handleBlur("instance")}
                        value={values.instance}
                        size="small"
                        fullWidth
                      />
                      <TextField
                        label={values.combine_count > 1 ? "Hashtag 1" : "Hashtag"}
                        placeholder="cybersecurity"
                        onChange={handleChange("hashtag")}
                        onBlur={handleBlur("hashtag")}
                        value={values.hashtag}
                        size="small"
                        fullWidth
                      />

                      <Button
                        onClick={() => {
                          const next = !showCombine;
                          setShowCombine(next);
                          if (!next) setFieldValue("combine_count", 1);
                        }}
                        size="small"
                        startIcon={<TuneIcon fontSize="small" />}
                        endIcon={
                          <ExpandMoreIcon
                            fontSize="small"
                            sx={{
                              transition: "transform 0.2s",
                              transform: showCombine ? "rotate(180deg)" : "none",
                            }}
                          />
                        }
                        sx={{ textTransform: "none", color: "text.secondary", px: 0.5, alignSelf: "flex-start" }}
                      >
                        Advanced hashtag search
                      </Button>
                      <Collapse in={showCombine}>
                        <Stack spacing={1.5}>
                      <FormControl size="small" fullWidth>
                        <InputLabel>Combine</InputLabel>
                        <Select
                          label="Combine"
                          value={values.combine_count}
                          onChange={(e) => {
                            const n = Number(e.target.value);
                            setFieldValue("combine_count", n);
                            if (n === 3 && (values.operator === "AND" || values.operator === "OR")) {
                              // keep simple AND/OR by default
                            }
                          }}
                        >
                          <MenuItem value={1}>1 hashtag</MenuItem>
                          <MenuItem value={2}>2 hashtags</MenuItem>
                          <MenuItem value={3}>3 hashtags</MenuItem>
                        </Select>
                      </FormControl>

                      {values.combine_count >= 2 && (
                        <>
                          <FormControl size="small" fullWidth>
                            <InputLabel>
                              {values.combine_count === 2 ? "Operator" : "Combination"}
                            </InputLabel>
                            <Select
                              label={values.combine_count === 2 ? "Operator" : "Combination"}
                              value={values.operator}
                              onChange={handleChange("operator")}
                            >
                              {values.combine_count === 2 ? (
                                [
                                  <MenuItem key="AND" value="AND">hashtag 1 <Box component="span" sx={{ color: "secondary.main", fontWeight: 700, mx: 0.5 }}>AND</Box> hashtag 2</MenuItem>,
                                  <MenuItem key="OR" value="OR">hashtag 1 <Box component="span" sx={{ color: "secondary.main", fontWeight: 700, mx: 0.5 }}>OR</Box> hashtag 2</MenuItem>,
                                ]
                              ) : (
                                [
                                  <MenuItem key="AND" value="AND">hashtag 1 <Box component="span" sx={{ color: "secondary.main", fontWeight: 700, mx: 0.5 }}>AND</Box> hashtag 2 <Box component="span" sx={{ color: "secondary.main", fontWeight: 700, mx: 0.5 }}>AND</Box> hashtag 3</MenuItem>,
                                  <MenuItem key="OR" value="OR">hashtag 1 <Box component="span" sx={{ color: "secondary.main", fontWeight: 700, mx: 0.5 }}>OR</Box> hashtag 2 <Box component="span" sx={{ color: "secondary.main", fontWeight: 700, mx: 0.5 }}>OR</Box> hashtag 3</MenuItem>,
                                  <MenuItem key="AND_OR" value="AND_OR">hashtag 1 <Box component="span" sx={{ color: "secondary.main", fontWeight: 700, mx: 0.5 }}>AND</Box> ( hashtag 2 <Box component="span" sx={{ color: "secondary.main", fontWeight: 700, mx: 0.5 }}>OR</Box> hashtag 3 )</MenuItem>,
                                  <MenuItem key="OR_AND" value="OR_AND">hashtag 1 <Box component="span" sx={{ color: "secondary.main", fontWeight: 700, mx: 0.5 }}>OR</Box> ( hashtag 2 <Box component="span" sx={{ color: "secondary.main", fontWeight: 700, mx: 0.5 }}>AND</Box> hashtag 3 )</MenuItem>,
                                ]
                              )}
                            </Select>
                          </FormControl>

                          <TextField
                            label="Hashtag 2"
                            placeholder="drones"
                            onChange={handleChange("hashtag2")}
                            onBlur={handleBlur("hashtag2")}
                            value={values.hashtag2}
                            size="small"
                            fullWidth
                          />
                        </>
                      )}

                      {values.combine_count === 3 && (
                        <TextField
                          label="Hashtag 3"
                          placeholder="iot"
                          onChange={handleChange("hashtag3")}
                          onBlur={handleBlur("hashtag3")}
                          value={values.hashtag3}
                          size="small"
                          fullWidth
                        />
                      )}
                        </Stack>
                      </Collapse>
                    </Stack>

                    <Box>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ letterSpacing: "0.08em", display: "block", mb: 1 }}
                      >
                        SCHEDULE
                      </Typography>
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "1fr 130px",
                          gap: 1.5,
                          alignItems: "center",
                        }}
                      >
                        <Tooltip title="Recrawl the source in specified intervals">
                          <FormControlLabel
                            sx={{ m: 0 }}
                            control={
                              <Switch
                                onChange={handleChange("monitoring")}
                                value={values.monitoring}
                                checked={values.monitoring}
                                size="small"
                              />
                            }
                            label="Monitoring"
                          />
                        </Tooltip>
                        <Tooltip title="Monitoring interval in days">
                          <TextField
                            onChange={handleChange("monitoring_interval")}
                            value={values.monitoring_interval}
                            type="number"
                            label="Interval (d)"
                            size="small"
                            disabled={!values.monitoring}
                            InputProps={{ inputProps: { min: 0 } }}
                          />
                        </Tooltip>
                      </Box>
                    </Box>

                    <Box>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ letterSpacing: "0.08em", display: "block", mb: 1 }}
                      >
                        FILTERING
                      </Typography>
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "1fr 130px",
                          gap: 1.5,
                          alignItems: "center",
                        }}
                      >
                        <Tooltip title="Enables focused crawler — discards low-confidence results from MISP">
                          <FormControlLabel
                            sx={{ m: 0 }}
                            control={
                              <Switch
                                onChange={handleChange("focused")}
                                value={values.focused}
                                checked={values.focused}
                                size="small"
                              />
                            }
                            label="Focused"
                          />
                        </Tooltip>
                        <Tooltip title="Confidence threshold (0–1)">
                          <TextField
                            onChange={handleChange("threshold")}
                            value={values.threshold}
                            type="number"
                            label="Threshold"
                            size="small"
                            disabled={!values.focused}
                            InputProps={{ inputProps: { min: 0, max: 1, step: 0.1 } }}
                          />
                        </Tooltip>
                      </Box>
                    </Box>

                    <Box>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ letterSpacing: "0.08em", display: "block", mb: 1 }}
                      >
                        LIMITS
                      </Typography>
                      <TextField
                        onChange={handleChange("post_limit")}
                        value={values.post_limit}
                        type="number"
                        label="Post limit"
                        size="small"
                        fullWidth
                        InputProps={{ inputProps: { min: 0, step: 1 } }}
                      />
                    </Box>

                    <Button
                      variant="contained"
                      onClick={handleSubmit}
                      disabled={!isValid}
                      startIcon={<PlayArrowIcon />}
                      fullWidth
                      sx={{ mt: 0.5 }}
                    >
                      Start crawl
                    </Button>
                  </Stack>
                )}
              </Formik>
            </FormGroup>
          </Card>
        </Grid>

        {/* Active tasks */}
        <Grid item xs={12} md={7} lg={8}>
          <SectionHeader title={`Active tasks${kpis.total ? ` (${kpis.total})` : ""}`} />

          {/* Filter bar */}
          {mastodonTasks.length > 0 && (
            <Card sx={{ p: 1.5, mb: 2 }}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1.5}
                alignItems={{ xs: "stretch", sm: "center" }}
              >
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={stateFilter}
                  onChange={(_, v) => { if (v) { setStateFilter(v); setPage(1); } }}
                  sx={{ flexWrap: "wrap" }}
                >
                  {STATE_FILTERS.map((f) => (
                    <ToggleButton key={f.key} value={f.key}>{f.label}</ToggleButton>
                  ))}
                </ToggleButtonGroup>
                <TextField
                  size="small"
                  placeholder="Search hashtag or instance…"
                  value={taskSearch}
                  onChange={(e) => { setTaskSearch(e.target.value); setPage(1); }}
                  sx={{ flex: 1, minWidth: 180 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" sx={{ color: "text.disabled" }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Stack>
            </Card>
          )}

          {mastodonTasks.length === 0 ? (
            <Card sx={{ p: 4, textAlign: "center" }}>
              <Typography variant="body2" color="text.disabled">
                No hashtags being tracked yet. Add one on the left to start.
              </Typography>
            </Card>
          ) : filteredTasks.length === 0 ? (
            <Card sx={{ p: 4, textAlign: "center" }}>
              <Typography variant="body2" color="text.disabled">
                No tasks match the current filters.
              </Typography>
            </Card>
          ) : (
            <>
              <Stack spacing={1.5}>
                {paginatedTasks.map((task) => (
                  <TaskCard
                    key={task.hashtag_id}
                    task={task}
                    onRun={submitMastodonTask}
                    onStop={killTask}
                    onSettings={openSettings}
                    onDelete={deleteTask}
                  />
                ))}
              </Stack>
              {filteredTasks.length > PAGE_SIZE && (
                <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                  <Pagination
                    count={Math.ceil(filteredTasks.length / PAGE_SIZE)}
                    page={page}
                    color="primary"
                    size="small"
                    onChange={(_, p) => setPage(p)}
                  />
                </Box>
              )}
            </>
          )}
        </Grid>
      </Grid>

      {activeTask && (
        <MastodonSettingsPopupForm
          key={activeTask.hashtag_id}
          open={openPopupForm}
          close={() => setOpenPopupForm(false)}
          task={activeTask}
          updateTaskParams={updateTaskParams}
        />
      )}
    </Box>
  );
};

export default MastodonTaskPage;
